export function getJwt() {
  return localStorage.getItem("jwt");
}

const AUTH_EVENT = "auth-changed";

function notifyAuthChange() {
  if (typeof window === "undefined") {
    return;
  }
  window.dispatchEvent(new Event(AUTH_EVENT));
}

export function setJwt(token) {
  if (token) {
    localStorage.setItem("jwt", token);
  } else {
    localStorage.removeItem("jwt");
  }
  notifyAuthChange();
}

export function getProfile() {
  const stored = localStorage.getItem("profile");
  if (!stored) {
    return null;
  }

  try {
    return JSON.parse(stored);
  } catch (error) {
    return null;
  }
}

const DEV_MODE =
  process.env.REACT_APP_DEV_MODE === "1" ||
  process.env.REACT_APP_DEV_MODE === "true";
const DEV_TOKEN = process.env.REACT_APP_DEV_AUTH_TOKEN || "";
const DEV_PROFILE_RAW = process.env.REACT_APP_DEV_PROFILE || "";

function buildDevProfile() {
  if (DEV_PROFILE_RAW) {
    try {
      const parsed = JSON.parse(DEV_PROFILE_RAW);
      if (parsed && typeof parsed === "object") {
        return { __dev: true, ...parsed };
      }
    } catch (error) {}
  }

  return {
    __dev: true,
    username: "nezqt3",
    first_name: "Алексеенко",
    last_name: "Денис",
    full_name: "Алексеенко Денис",
    role: "Руководитель проектов",
    level_of_access: "admin",
    telegram_id: "1108856135",
  };
}

export function isDevAuthEnabled() {
  return DEV_MODE && DEV_TOKEN.trim() !== "";
}

export function ensureDevAuth() {
  if (!isDevAuthEnabled()) {
    return;
  }
  const token = DEV_TOKEN.trim();
  if (!token) {
    return;
  }

  if (localStorage.getItem("jwt") !== token) {
    localStorage.setItem("jwt", token);
    notifyAuthChange();
  }

  const profile = getProfile();
  if (!profile || profile.__dev !== true) {
    localStorage.setItem("profile", JSON.stringify(buildDevProfile()));
    notifyAuthChange();
  }
}

const AVATAR_CACHE_KEY = "avatar_cache";

export function storeProfile(profile) {
  if (!profile) {
    return;
  }
  localStorage.setItem("profile", JSON.stringify(profile));
  cacheAvatar(profile);
  notifyAuthChange();
}

export function cacheAvatar(profile) {
  const photoUrl = profile?.photo_url;
  if (!photoUrl) {
    return;
  }
  const cache = getAvatarCache();
  const keys = [];
  if (profile?.telegram_id) {
    keys.push(String(profile.telegram_id));
  }
  if (profile?.username) {
    keys.push(profile.username.toLowerCase().replace(/^@/, ""));
  }
  if (keys.length === 0) {
    return;
  }
  keys.forEach((key) => {
    cache[key] = photoUrl;
  });
  localStorage.setItem(AVATAR_CACHE_KEY, JSON.stringify(cache));
}

export function getAvatarFromCache({ telegramId, username }) {
  const cache = getAvatarCache();
  const normalizedUsername = username
    ? String(username).toLowerCase().replace(/^@/, "")
    : "";
  if (telegramId && cache[String(telegramId)]) {
    return cache[String(telegramId)];
  }
  if (normalizedUsername && cache[normalizedUsername]) {
    return cache[normalizedUsername];
  }
  return "";
}

function getAvatarCache() {
  const stored = localStorage.getItem(AVATAR_CACHE_KEY);
  if (!stored) {
    return {};
  }
  try {
    return JSON.parse(stored) || {};
  } catch (error) {
    return {};
  }
}

export function getAuthHeaders() {
  const jwt = getJwt();
  if (!jwt) {
    return {};
  }
  return { Authorization: `Bearer ${jwt}` };
}

export function clearAuth() {
  localStorage.removeItem("jwt");
  localStorage.removeItem("profile");
  notifyAuthChange();
}

export function handleUnauthorized() {
  clearAuth();
  if (window.location.pathname !== "/") {
    window.location.replace("/");
  }
}

export function subscribeAuthChange(handler) {
  if (typeof window === "undefined") {
    return () => {};
  }
  window.addEventListener(AUTH_EVENT, handler);
  return () => window.removeEventListener(AUTH_EVENT, handler);
}

export function parseRoles(role = "") {
  return role
    .toLowerCase()
    .split(/[\\s,;/|+]+/)
    .map((item) => item.trim())
    .filter(Boolean);
}

export function normalizeAccessLevel(value = "", roleFallback = "") {
  const normalized = value.toString().trim().toLowerCase();
  if (
    ["админ", "admin", "administrator", "владелец", "owner"].includes(
      normalized,
    )
  ) {
    return "admin";
  }
  if (["модератор", "moderator"].includes(normalized)) {
    return "moderator";
  }
  if (["участник", "member"].includes(normalized)) {
    return "member";
  }
  const roles = parseRoles(roleFallback);
  if (
    roles.includes("админ") ||
    roles.includes("admin") ||
    roles.includes("владелец") ||
    roles.includes("owner")
  ) {
    return "admin";
  }
  if (roles.includes("модератор") || roles.includes("moderator")) {
    return "moderator";
  }
  return "member";
}

export function getEffectiveAccessLevel(profile) {
  return normalizeAccessLevel(
    profile?.level_of_access || "",
    profile?.role || "",
  );
}

export function isAdmin(accessLevel = "", roleFallback = "") {
  return (
    normalizeAccessLevel(accessLevel, roleFallback || accessLevel) === "admin"
  );
}

export function isModerator(accessLevel = "", roleFallback = "") {
  return (
    normalizeAccessLevel(accessLevel, roleFallback || accessLevel) ===
    "moderator"
  );
}

export function isLeader(role = "") {
  const roles = parseRoles(role);
  return roles.includes("хелпер") || roles.includes("helper");
}

export function canManageMembers(accessLevel = "") {
  return isAdmin(accessLevel);
}

export function canManageTasks(accessLevel = "", memberRole = "") {
  return (
    isAdmin(accessLevel) || isModerator(accessLevel) || isLeader(memberRole)
  );
}

export function canReview(accessLevel = "", memberRole = "") {
  return canManageTasks(accessLevel, memberRole);
}

export function stripAccessFromRole(role = "") {
  const parts = role
    .split(/[,;/|+]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  const cleaned = parts.filter(
    (part) =>
      ![
        "админ",
        "admin",
        "administrator",
        "модератор",
        "moderator",
        "владелец",
        "owner",
      ].includes(part.toLowerCase()),
  );
  return cleaned.join(", ");
}

export function normalizeProjectRole(role = "") {
  const cleaned = stripAccessFromRole(role);
  const parts = cleaned
    .split(/[,;/|+]+/)
    .map((item) => item.trim())
    .filter(Boolean);
  const baseParts = [];
  let helper = false;
  parts.forEach((part) => {
    const lower = part.toLowerCase();
    if (["хелпер", "helper"].includes(lower)) {
      helper = true;
      return;
    }
    baseParts.push(part);
  });
  if (helper) {
    baseParts.push("Хелпер");
  }
  return baseParts.join(", ");
}
